// pages/ToshbaHalachaBookSubtopicDetail.js
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchWithAuth } from '../auth';
import Breadcrumbs from '../components/Breadcrumbs';
import { SkeletonCardGrid } from '../components/ui';
import { useResourceDetailModal } from '../components/ResourceDetailModalContext';
import Accordion from '../components/Accordion';
import AdditionalResourcesAccordion from '../components/AdditionalResourcesAccordion';

export default function ToshbaHalachaBookSubtopicDetail() {
  const { domain, subtopicId, bookId, id } = useParams();
  const [bookSubtopic, setBookSubtopic] = useState(null);
  const [book, setBook] = useState(null);
  const [subtopic, setSubtopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        // Get book subtopic details (includes resources nested)
        const res = await fetchWithAuth(`/api/subtopic-halacha-book-subtopics/${id}/`);
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        if (!ignore) setBookSubtopic(data);

        // Get book name
        const bookRes = await fetchWithAuth(`/api/subtopic-halacha-books/${bookId}/`);
        if (bookRes.ok) {
          const bookData = await bookRes.json();
          if (!ignore) setBook(bookData);
        }

        // Get subtopic name
        const subtopicRes = await fetchWithAuth(`/api/subtopic-halacha/${subtopicId}/`);
        if (subtopicRes.ok) {
          const subtopicData = await subtopicRes.json();
          if (!ignore) setSubtopic(subtopicData);
        }
      } catch {
        if (!ignore) setError('שגיאה בטעינת פרטי הנושא המשני');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id, bookId, subtopicId]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="h-6 w-64 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mb-6" />
        <div className="h-8 w-32 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mx-auto mb-8" />
        <SkeletonCardGrid count={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!bookSubtopic) return null;

  const resourceTypeLabels = {
    'TEACHER_GUIDE': 'מדריכים למורה',
    'PRESENTATIONS': 'מצגות',
    'WORKSHEETS': 'דפי עבודה',
    'QUIZZES': 'בחנים',
    'TESTS': 'מבחנים',
    'IMAGES': 'תמונות',
    'VIDEOS': 'סרטונים',
    'GAMES': 'משחקים',
    'PODCASTS': 'פודקאסטים'
  };

  const TYPE_ORDER = [
    'TEACHER_GUIDE',
    'PRESENTATIONS',
    'WORKSHEETS',
    'QUIZZES',
    'TESTS',
    'IMAGES',
    'VIDEOS',
    'GAMES',
    'PODCASTS',
  ];

  const groupedResources = (bookSubtopic.resources || []).reduce((acc, r) => {
    const type = r.resource_type || 'OTHER';
    if (!acc[type]) acc[type] = [];
    acc[type].push(r);
    return acc;
  }, {});

  const orderedTypeKeys = Object.keys(groupedResources).sort((a, b) => {
    const ia = TYPE_ORDER.indexOf(a);
    const ib = TYPE_ORDER.indexOf(b);
    const iaSafe = ia === -1 ? TYPE_ORDER.length : ia;
    const ibSafe = ib === -1 ? TYPE_ORDER.length : ib;
    return iaSafe - ibSafe;
  });

  return (
    <div className="page-wrapper">
      <Breadcrumbs
        items={[
          { to: '/disciplines', label: 'תחומי דעת' },
          { to: '/toshba/classes', label: 'תושב"ע' },
          { to: `/toshba/classes/${domain}`, label: 'הלכה' },
          { to: `/toshba/classes/${domain}/${subtopicId}`, label: subtopic?.name || '...' },
          { to: `/toshba/classes/${domain}/${subtopicId}/books/${bookId}`, label: book?.name || '...' },
          { label: bookSubtopic.name },
        ]}
      />

      <div className="toolbar-center">
        <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 dark:text-white">
          {bookSubtopic.name}
        </h2>
      </div>

      <p className="text-center mt-2 mb-6">
        <Link
          to={`/toshba/classes/${domain}/${subtopicId}/books/${bookId}`}
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          חזרה לספר
        </Link>
      </p>

      {/* Resources grouped by type */}
      {Object.keys(groupedResources).length > 0 && (
        <section className="mt-8">
          <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">משאבים</h3>
          {orderedTypeKeys.map((type) => {
            const items = groupedResources[type];
            if (!items || items.length === 0) return null;
            return (
            <div key={type} className="mt-6">
              <h4 className="text-lg font-medium text-secondary-800 dark:text-secondary-200 mb-3">
                {resourceTypeLabels[type] || type}
              </h4>
              <div className="grid-autofit">
                {items.map(resource => (
                  <ResourceCard key={resource.id} item={resource} />
                ))}
              </div>
            </div>
            );
          })}
        </section>
      )}

      {Object.keys(groupedResources).length === 0 && (
        <p className="text-center text-secondary-500 dark:text-secondary-400 py-8">
          אין משאבים להצגה.
        </p>
      )}

      {/* Additional Resources Accordion */}
      {bookSubtopic.additional_resources && bookSubtopic.additional_resources.length > 0 && (
        <section className="mt-8">
          <AdditionalResourcesAccordion resources={bookSubtopic.additional_resources} />
        </section>
      )}
    </div>
  );
}

function ResourceCard({ item }) {
  const { openResourceDetail } = useResourceDetailModal();
  const hasFile = !!item.file;
  const isExternal = !!item.url && !hasFile;
  const downloadUrl = hasFile
    ? `/api/subtopic-halacha-book-subtopic-resources/${item.id}/download/`
    : item.url || item.file;

  const handleOpen = () => {
    if (!item.use_detail_modal && downloadUrl) {
      window.open(downloadUrl, isExternal ? '_blank' : '_self', isExternal ? 'noopener,noreferrer' : undefined);
      return;
    }

    const modalItem = {
      id: item.id,
      model_type: 'toshba_halacha_book_subtopic_resource',
      title: item.title || 'משאב ללא כותרת',
      summary: item.explanation || '',
      explanation: item.explanation || '',
      author: item.author || '',
      subjectLabel: 'תושב״ע - הלכה',
      typeLabel: 'משאב',
      grades: item.grades || [],
      tags: [],
      downloadUrl,
      date: item.created_at || '',
    };
    openResourceDetail(modalItem);
  };

  return (
    <article className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1">
      <button
        type="button"
        onClick={handleOpen}
        className="block w-full relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
        style={{ aspectRatio: '3/2' }}
      >
        {item.image ? (
          <img src={item.image} alt={item.title || 'משאב'} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        )}
      </button>
      <div className="p-4 md:p-5">
        <h4 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
          <button
            type="button"
            onClick={handleOpen}
            className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-right w-full text-inherit"
          >
            {item.title || 'משאב ללא כותרת'}
          </button>
        </h4>
        {item.author && <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">{item.author}</p>}
      </div>
    </article>
  );
}